/**
 * IncidentExportActions Component - Phase 13
 * 
 * Export controls for incident data.
 */

import { useState } from 'react';
import {
  Download,
  FileSpreadsheet,
  FileJson,
  Copy,
  Check,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';

/**
 * IncidentExportActions Component
 */
export function IncidentExportActions({
  incidentCount,
  filters,
  onExportCsv,
  onExportJson,
  onCopySummary,
  disabled
}) {
  const [exporting, setExporting] = useState(null);
  const [copied, setCopied] = useState(false);

  // Count active filters
  const activeFilterCount = Object.entries(filters || {}).filter(([key, value]) => {
    if (key === 'sortBy') return false;
    if (key === 'searchText') return !!value;
    if (key === 'unresolvedOnly') return value === true;
    return value !== null;
  }).length;

  const handleExportCsv = async () => {
    setExporting('csv');
    try {
      const result = await onExportCsv();
      if (result?.success) {
        toast.success(`Exported ${result.count} incidents to CSV`);
      } else {
        toast.error('Export failed');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setExporting(null);
    }
  };

  const handleExportJson = async () => {
    setExporting('json');
    try {
      const result = await onExportJson();
      if (result?.success) {
        toast.success(`Exported ${result.count} incidents to JSON`);
      } else {
        toast.error('Export failed');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setExporting(null);
    }
  };

  const handleCopySummary = async () => {
    try {
      const result = await onCopySummary();
      if (result) {
        setCopied(true);
        toast.success('Summary copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
      } else {
        toast.error('Failed to copy');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <div className="flex items-center gap-2" data-testid="incident-export-actions">
      {/* Export info badge */}
      <Badge variant="outline" className="text-xs">
        {incidentCount} incident{incidentCount !== 1 ? 's' : ''}
        {activeFilterCount > 0 && ` (${activeFilterCount} filter${activeFilterCount !== 1 ? 's' : ''})`}
      </Badge>

      {/* Export dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" disabled={disabled || incidentCount === 0}>
            {exporting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Export
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleExportCsv} disabled={exporting === 'csv'}>
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Export as CSV
            {exporting === 'csv' && <Loader2 className="h-3 w-3 animate-spin ml-auto" />}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleExportJson} disabled={exporting === 'json'}>
            <FileJson className="h-4 w-4 mr-2" />
            Export as JSON
            {exporting === 'json' && <Loader2 className="h-3 w-3 animate-spin ml-auto" />}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleCopySummary}>
            {copied ? (
              <Check className="h-4 w-4 mr-2 text-green-500" />
            ) : (
              <Copy className="h-4 w-4 mr-2" />
            )}
            Copy Summary
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export default IncidentExportActions;
